/**
 * Format: Algolia
 * -----------------------------------------------------------------------------
 * Formats Algolia responses for product cards.
 *
 * METAFIELDS TO INDEX IN ALGOLIA DASHBOARD:
 * ==========================================
 * Add these metafields to Shopify Algolia settings
 *
 * GLOBAL NAMESPACE:
 * - pre_order (Date: Pre-Order Available Date)
 * - promo_tag (String: Promo Tag)
 * - promo_tag_alt_style (Boolean: Promo Tag Alt Style)
 * - badge_1 (String: Badge 1)
 * - badge_2 (String: Badge 2)
 * - personalised_cross_sell (Boolean: Personalised Cross Sell)
 * - height (String: Height)
 * - diameter (String: Diameter)
 * - length (String: Length)
 * - capacity (String: Capacity)
 * - care_instructions (String: Care Instructions)
 * - manufacturer_country (String: Manufacturer Country)
 * - made_from (String: Made From)
 * - personalisation_intro (String: Personalisation Intro)
 * - front_body_characters (Number: Front Body Characters)
 * - back_body_characters (Number: Back Body Characters)
 * - top_edge_characters (Number: Top Edge Characters)
 * - center_characters (Number: Center Characters)
 * - bottom_edge_characters (Number: Bottom Edge Characters)
 * - base_characters (Number: Base Characters)
 * - hide_usps (Boolean: Hide USPs)
 * - hidden_promo_content (Boolean: Hidden Promo Content)
 * - badge_style_alt (Boolean: Badge Style Alt)
 *
 * CUSTOM NAMESPACE:
 * - pre_order_launch_date (Date and time: Pre-Order Launch Date)
 * - pre_order_closing_date (Date: Pre-Order Closing Date)
 * - pre_order_available_message (String: Pre-Order Available Message)
 * - pre_order_closing_message (String: Pre-Order Closing Message)
 * - available_to_buy_date_time (Date and time: Available To Buy Date/Time)
 *
 * BOOKTHATAPP NAMESPACE:
 * - config (JSON: BookThatApp Config)
 * - json_config (JSON: BookThatApp JSON Config)
 */
import { formatPriceAmount, resolveAlgoliaMarketPrice } from '~/format/format-price'
import { handleDate } from '~/helpers/general'

const ALGOLIA_PRICE_FIELDS = {
  priceMin: {
    market: ['variant_min_price', 'variants_min_price', 'price'],
    fallback: ['variants_min_price', 'variant_min_price', 'price'],
  },
  priceMax: {
    market: ['variant_max_price', 'variants_max_price', 'price'],
    fallback: ['variants_max_price', 'variant_max_price', 'price'],
  },
  compareAt: {
    market: ['compare_at_price', 'variant_min_compare_at_price', 'variants_compare_at_price_min'],
    fallback: ['compare_at_price', 'variants_compare_at_price_min', 'variant_min_compare_at_price'],
  },
  compareAtMin: {
    market: ['variant_min_compare_at_price', 'variants_compare_at_price_min', 'compare_at_price'],
    fallback: ['variants_compare_at_price_min', 'variant_min_compare_at_price', 'compare_at_price'],
  },
  compareAtMax: {
    market: ['variant_max_compare_at_price', 'variants_compare_at_price_max', 'compare_at_price'],
    fallback: ['variants_compare_at_price_max', 'variant_max_compare_at_price', 'compare_at_price'],
  },
}

/**
 * Format Algolia product hit into the shape expected by product-card.
 * @param {Object} response - Algolia hit data.
 * @returns {Object}
 */
export function formatAlgoliaProduct(response) {
  const product = response
  const url = `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`

  const metafields = formatAlgoliaMetafields(product)
  const tags = formatAlgoliaTags(product)
  const { _highlightResult: highlightResult = {} } = product

  // Missing inventory_management should not override regular products.
  const hasNoInventoryTracking = [null, '', false].includes(product.inventory_management)
  const isBookingProduct = isAlgoliaBookingProduct(product, metafields, tags)
  const isGiftCard = isAlgoliaGiftCard(product)
  const allowsOversell = product.inventory_policy === 'continue'

  let isAvailable = false
  if (isBookingProduct || isGiftCard || hasNoInventoryTracking || allowsOversell) {
    isAvailable = true
  } else if (product.inventory_available === true) {
    isAvailable = true
  } else if (product.inventory_available === false) {
    isAvailable = false
  }

  const formattedProduct = {
    available: isAvailable,
    badges: formatAlgoliaBadges(isAvailable, metafields, tags),
    compareAtPrice: formatAlgoliaCompareAtPrice(product),
    currentDateTime: new Date(),
    description: product.body_html_safe,
    giftCard: product.is_gift_card || false,
    handle: product.handle,
    // Algolia returns single variant per hit
    hasOnlyDefaultVariant: true,
    id: product.id,
    objectID: product.objectID ? String(product.objectID) : '',
    images: formatAlgoliaImages(product),
    limits: formatAlgoliaLimits(tags),
    metafields,
    options: null,
    personalisation: formatAlgoliaPersonalisation(tags, metafields),
    personalisationCrossSell: metafields?.personalisedCrossSell || false,
    price: formatAlgoliaPrice(product),
    promoTag: metafields?.promoTag || false,
    promoTagAltStyle: Boolean(metafields?.promoTagAltStyle),
    // Not needed for PLP
    specifications: [],
    tags,
    title: highlightResult?.title?.value || product.title,
    type: product.product_type,
    url,
    variantId: product.objectID ? Number(product.objectID) : null,
    vendor: product.vendor,
  }

  Object.defineProperty(formattedProduct, '_highlightResult', {
    configurable: true,
    enumerable: true,
    value: highlightResult,
    writable: true,
  })

  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.secondaryImage = {}

  // Use 'Default Title' to match PDP format and allow cart line items to merge
  const selectedOptions = [{ name: 'Title', value: 'Default Title' }]

  // Build variant URL to match format-variant.js
  const variantUrl = `${url}?variant=${formattedProduct.variantId}`

  const variant = {
    available: formattedProduct.available,
    compareAtPrice: formattedProduct.compareAtPrice,
    id: formattedProduct.variantId,
    image: formattedProduct.featuredImage,
    inventory: product.quantity_available || 0,
    price: formattedProduct.price,
    selectedOptions,
    sku: product.sku || '',
    // Match PDP variant title format
    title: 'Default Title',
    url: variantUrl,
  }

  formattedProduct.variants = [variant]
  formattedProduct.options = [
    {
      index: 0,
      labels: ['Default Title'],
      name: 'Title',
      values: ['Default Title'],
    },
  ]
  formattedProduct.firstAvailableVariant = variant
  formattedProduct.selectedOrFirstAvailableVariant = variant

  return formattedProduct
}

function isAlgoliaBookingProduct(product, metafields, tags) {
  const bookthatappFields = [
    metafields?.bookthatapp,
    metafields?.bookthatappJsonConfig,
    product.meta?.bookthatapp?.config,
    product.meta?.bookthatapp?.json_config,
    product.metafields?.bookthatapp?.config,
    product.metafields?.bookthatapp?.json_config,
    product.bookthatapp_config,
    product.bookthatapp_json_config,
    product.metafield_bookthatapp_config,
    product.metafield_bookthatapp_json_config,
    product.meta_bookthatapp_config,
    product.meta_bookthatapp_json_config,
  ]

  const hasBookthatappField = bookthatappFields.some((value) => {
    return value && value !== 'false'
  })

  if (hasBookthatappField) {
    return true
  }

  const searchableValues = [
    product.product_type,
    product.type,
    product.handle,
    product.title,
    ...tags,
  ].filter(Boolean).join(' ').toLowerCase()

  return searchableValues.includes('factory') &&
    (searchableValues.includes('experience') || searchableValues.includes('ticket'))
}

function isAlgoliaGiftCard(product) {
  if (product.is_gift_card || product.gift_card || product.giftCard || product.isGiftCard) {
    return true
  }

  const searchableValues = [
    product.product_type,
    product.type,
    product.handle,
    product.title,
  ].filter(Boolean).join(' ').toLowerCase()

  return searchableValues.includes('gift card') ||
    searchableValues.includes('gift-card') ||
    searchableValues.includes('gift voucher') ||
    searchableValues.includes('gift-voucher')
}

/**
 * Format badges from Algolia product data.
 * - Mirrors the logic from formatProductBadges in format-product.js
 * @param {Boolean} available - Product availability (calculated).
 * @param {Object} metafields - Formatted metafields.
 * @param {Array} tags - Product tags.
 * @returns {Array}
 */
function formatAlgoliaBadges(available, metafields, tags) {
  const badges = []

  // Handle Pre-Order badge
  let isPreOrder = false
  let isPreorderLaunch = false

  const hasPreOrderTag = tags?.some((tag) => tag.includes('Pre-Order'))
  const preOrderAvailableDate = metafields?.preOrderAvailableDate
  const preOrderLaunchDate = metafields?.preOrderLaunchDate

  if (hasPreOrderTag) {
    isPreOrder = true
  }

  if (preOrderLaunchDate && handleDate('future-time', preOrderLaunchDate)) {
    isPreorderLaunch = true
  }

  if (
    handleDate('current-date', preOrderAvailableDate) ||
    handleDate('past-date', preOrderAvailableDate)
  ) {
    isPreOrder = false
  }

  // Push custom badges (filter out 'false' string values)
  if (metafields?.badge1 && metafields.badge1 !== 'false') {
    badges.push(metafields.badge1)
  }

  if (metafields?.badge2 && metafields.badge2 !== 'false') {
    badges.push(metafields.badge2)
  }

  // Handle Coming Soon (non-preorder products with available_to_buy_date)
  const availableToBuyDate = metafields?.availableToBuyDate
  const isComingSoon = !hasPreOrderTag && availableToBuyDate && handleDate('future-time', availableToBuyDate)

  // Handle Out of Stock - use calculated available value (matches PDP)
  if (!available && !isPreorderLaunch && !isComingSoon) {
    badges.push('oos')
  }

  // Add pre-order badge
  if (isPreOrder) {
    badges.push('pre-order')
  }

  return badges
}

/**
 * Format product limits from tags.
 * - Mirrors formatProductDefaultLimits from format-product.js
 * @param {Array} tags - Product tags.
 * @returns {Object}
 */
function formatAlgoliaLimits(tags) {
  const limitTag = tags?.find((tag) => tag.includes('LIMIT_PER_ORDER:'))
  const limitValue = limitTag ? parseInt(limitTag.split(':')[1]) : 999

  return {
    collectors: tags?.some((tag) => tag.includes('COLLECTORS_ONLY')) || false,
    collectorsEarly: tags?.some((tag) => tag.includes('COLLECTORS_EARLY')) || false,
    collectorsPreview: tags?.some((tag) => tag.includes('COLLECTORS_PREVIEW')) || false,
    inCart: false,
    onePerOrder: tags?.some((tag) => tag.includes('LIMIT_PER_ORDER')) || false,
    perCustomer: limitValue,
    purchasedPreviously: false,
    remaining: limitValue,
  }
}

/**
 * Format personalisation data from tags.
 * - Mirrors formatProductPersonalisation from format-product.js
 * @param {Array} tags - Product tags.
 * @param {Object} metafields - Formatted metafields.
 * @returns {Object}
 */
function formatAlgoliaPersonalisation(tags, metafields) {
  const isEnabled = tags?.some((tag) => {
    return ['MARK_IF_PERSONALISED: Yes', 'NATURE: Personalised Pottery'].includes(tag)
  }) || false

  const tagPrefix = 'PERSONALISATION:'
  const locationTags = tags?.filter((tag) => tag.startsWith(tagPrefix)) || []

  const locations = locationTags.map((tag) => {
    const name = tag.split(tagPrefix)[1]?.trim() || ''
    // Get character limit from metafields if available
    const metafieldKey = `${name.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')}_characters`
    const characterLimit = metafields?.[metafieldKey] || 20
    return {
      name,
      characterLimit: Number(characterLimit),
    }
  })

  return {
    enabled: isEnabled,
    locations,
    introMessage: metafields?.personalisationIntro || '',
  }
}

/**
 * Format Algolia product price.
 * @returns {Object}
 */
function formatAlgoliaPrice(product) {
  const currencyCode = cnvs.store.currency
  const minAmount = resolveAlgoliaPriceValue(product, currencyCode, ALGOLIA_PRICE_FIELDS.priceMin)
  const maxAmount = resolveAlgoliaPriceValue(product, currencyCode, ALGOLIA_PRICE_FIELDS.priceMax)

  return {
    amount: formatPriceAmount(minAmount),
    currencyCode,
    max: formatPriceAmount(maxAmount),
    min: formatPriceAmount(minAmount),
    varies: minAmount !== null && maxAmount !== null
      ? maxAmount !== minAmount
      : product.variants_max_price !== product.variants_min_price,
  }
}

/**
 * Format Algolia compare at price.
 * @param {Object} product - Algolia hit data.
 * @returns {Object}
 */
function formatAlgoliaCompareAtPrice(product) {
  const currencyCode = cnvs.store.currency
  const compareAtAmount = resolveAlgoliaPriceValue(
    product,
    currencyCode,
    ALGOLIA_PRICE_FIELDS.compareAt,
  )
  const compareAtMin = resolveAlgoliaPriceValue(
    product,
    currencyCode,
    ALGOLIA_PRICE_FIELDS.compareAtMin,
  )
  const compareAtMax = resolveAlgoliaPriceValue(
    product,
    currencyCode,
    ALGOLIA_PRICE_FIELDS.compareAtMax,
  )

  return {
    amount: formatPriceAmount(compareAtAmount),
    currencyCode,
    max: formatPriceAmount(compareAtMax),
    min: formatPriceAmount(compareAtMin),
    varies: compareAtMin !== null && compareAtMax !== null
      ? compareAtMax !== compareAtMin
      : product.variants_compare_at_price_max !== product.variants_compare_at_price_min,
  }
}

/**
 * Format Algolia image data.
 * @param {Object} product - Algolia hit data.
 * @returns {Array}
 */
function formatAlgoliaImages(product) {
  const src = product.image || product.product_image || null

  if (!src) {
    return [{ alt: product.title || '', index: 0, src: null }]
  }

  return [
    {
      alt: product.title || '',
      index: 0,
      src,
    },
  ]
}

/**
 * Format product tags from Algolia hit.
 * - Merges standard tags with converted named tags.
 * @param {Object} product - Algolia hit data.
 * @returns {Array}
 */
function formatAlgoliaTags(product) {
  const tags = product.tags ? [...product.tags] : []
  const namedTags = convertNamedTags(product)
  return tags.concat(namedTags)
}

/**
 * Convert Algolia named tags to standard tag format.
 * - Algolia stores some tags as key-value pairs in `named_tags`.
 * - Converts them back to `key:value` string format.
 * @param {Object} product - Algolia hit data.
 * @returns {Array}
 */
function convertNamedTags(product) {
  const convertedTags = []

  if (!product.named_tags) {
    return convertedTags
  }

  Object.entries(product.named_tags).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((arrayValue) => {
        convertedTags.push(`${key}:${arrayValue}`)
      })
      return
    }

    convertedTags.push(`${key}:${value}`)
  })

  return convertedTags
}

/**
 * Resolve a market-aware Algolia price value using shared candidate config.
 * @param {Object} product - Algolia hit data.
 * @param {String} currencyCode - Active storefront currency.
 * @param {Object} fields - Candidate fields config.
 * @returns {Number|null}
 */
function resolveAlgoliaPriceValue(product, currencyCode, fields) {
  return resolveAlgoliaMarketPrice(product, {
    currencyCode,
    marketFieldCandidates: fields.market,
    fallbackFieldCandidates: fields.fallback,
  })
}

/**
 * Format Algolia metafields into the shape expected by product components.
 * - Algolia may store metafields in different structures
 *   depending on configuration.
 * - Tries multiple possible paths:
 *   meta.namespace.key, metafields.namespace.key, flat keys.
 * @param {Object} product - Algolia hit data.
 * @returns {Object}
 */
function formatAlgoliaMetafields(product) {
  // Try different structures that Algolia might use
  const meta = product.meta || {}
  const metafields = product.metafields || {}
  const globalMeta = meta.global || metafields.global || {}
  const customMeta = meta.custom || metafields.custom || {}
  const bookthatappMeta = meta.bookthatapp || metafields.bookthatapp || {}

  // Also check for flat metafield keys (common in some Algolia configs)
  // Filter out string 'false' values to match format-product.js behavior
  const getMetafieldValue = (namespace, key) => {
    let value = null

    // Try nested structures first
    if (namespace === 'global' && globalMeta[key]) {
      value = globalMeta[key]
    } else if (namespace === 'custom' && customMeta[key]) {
      value = customMeta[key]
    } else if (namespace === 'bookthatapp' && bookthatappMeta[key]) {
      value = bookthatappMeta[key]
    } else if (product[`${namespace}_${key}`]) {
      // Try flat key patterns
      value = product[`${namespace}_${key}`]
    } else if (product[`metafield_${namespace}_${key}`]) {
      value = product[`metafield_${namespace}_${key}`]
    } else if (product[`meta_${namespace}_${key}`]) {
      value = product[`meta_${namespace}_${key}`]
    }

    // Filter out string 'false' values (matches format-product.js behavior)
    if (value === 'false' || value === null || typeof value === 'undefined') {
      return false
    }

    return value
  }

  return {
    // Pre-Order fields
    preOrderAvailableDate: getMetafieldValue('global', 'pre_order'),
    preOrderClosingDate: getMetafieldValue('custom', 'pre_order_closing_date'),
    preOrderLaunchDate: getMetafieldValue('custom', 'pre_order_launch_date'),
    preOrderAvailableMessage: getMetafieldValue('custom', 'pre_order_available_message'),
    preOrderClosingMessage: getMetafieldValue('custom', 'pre_order_closing_message'),
    availableToBuyDate: getMetafieldValue('custom', 'available_to_buy_date_time'),

    // Promo & Badge fields
    promoTag: getMetafieldValue('global', 'promo_tag'),
    promoTagAltStyle: getMetafieldValue('global', 'promo_tag_alt_style'),
    badge1: getMetafieldValue('global', 'badge_1'),
    badge2: getMetafieldValue('global', 'badge_2'),
    badgeStyleAlt: getMetafieldValue('global', 'badge_style_alt'),

    // Product Detail fields
    height: getMetafieldValue('global', 'height'),
    diameter: getMetafieldValue('global', 'diameter'),
    length: getMetafieldValue('global', 'length'),
    capacity: getMetafieldValue('global', 'capacity'),
    careInstructions: getMetafieldValue('global', 'care_instructions'),
    manufacturerCountry: getMetafieldValue('global', 'manufacturer_country'),
    madeFrom: getMetafieldValue('global', 'made_from'),

    // Personalisation fields
    personalisationIntro: getMetafieldValue('global', 'personalisation_intro'),
    frontBodyCharacters: getMetafieldValue('global', 'front_body_characters'),
    backBodyCharacters: getMetafieldValue('global', 'back_body_characters'),
    topEdgeCharacters: getMetafieldValue('global', 'top_edge_characters'),
    centerCharacters: getMetafieldValue('global', 'center_characters'),
    bottomEdgeCharacters: getMetafieldValue('global', 'bottom_edge_characters'),
    baseCharacters: getMetafieldValue('global', 'base_characters'),
    personalisedCrossSell: getMetafieldValue('global', 'personalised_cross_sell'),

    // Visibility & UX fields
    hideUsps: getMetafieldValue('global', 'hide_usps'),
    hiddenPromoContent: getMetafieldValue('global', 'hidden_promo_content'),

    // Third-party integrations
    bookthatapp: getMetafieldValue('bookthatapp', 'config'),
    bookthatappJsonConfig: getMetafieldValue('bookthatapp', 'json_config'),
  }
}
